import React, { useState, useEffect, useCallback } from 'react';
import { Users, Calendar, Clock, CheckCircle2, XCircle, AlertTriangle, Key } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { stammAPI } from '../services/api';
import Toast from '../components/Toast';

const inp = { width:'100%', padding:'8px 11px', border:'1px solid #D1D5DB', borderRadius:7, fontSize:'0.875rem', outline:'none', boxSizing:'border-box', color:'#111827', backgroundColor:'white' };
const lbl = { display:'block', fontSize:'0.75rem', fontWeight:700, color:'#374151', marginBottom:4 };

const VISIBLE_DEFAULT = 6;

const StammPage = () => {
  const { isAuthenticated, user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [mySessions, setMySessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [proposeModal, setProposeModal] = useState(null);
  const [showMySessions, setShowMySessions] = useState(false);
  const [nom, setNom] = useState('');
  const [indicatif, setIndicatif] = useState('');

  const showToast = (msg, type = 'success') => setToast({ message: msg, type });

  useEffect(() => {
    document.body.style.overflow = proposeModal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [proposeModal]);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        stammAPI.getAll(),
        isAuthenticated ? stammAPI.getMy() : Promise.resolve({ data: [] }),
      ]);
      setSessions(r1.data);
      setMySessions(r2.data);
    } catch {
      showToast('Erreur de chargement', 'error');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  const handleProposer = async () => {
    if (!proposeModal) return;
    setSubmitting(true);
    try {
      const body = isAuthenticated ? {} : { nom_libre: nom.trim(), indicatif_libre: indicatif.trim() };
      await stammAPI.proposer(proposeModal.id, body);
      showToast('Candidature transmise au comité — en attente de validation.');
      setProposeModal(null);
      setNom(''); setIndicatif('');
      load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Erreur', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetirer = async (sessionId) => {
    try {
      await stammAPI.retirer(sessionId);
      showToast('Candidature retirée.');
      load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Erreur', 'error');
    }
  };

  if (loading) return (
    <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:200,color:'#9CA3AF',gap:10}}>
      <Clock style={{width:18,height:18}}/> Chargement…
    </div>
  );

  const visibleSessions = showAll ? sessions : sessions.slice(0, VISIBLE_DEFAULT);
  const hasMore = sessions.length > VISIBLE_DEFAULT;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>

      {/* En-tête */}
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <Users style={{width:20,height:20,color:'#6366F1',flexShrink:0}}/>
        <h1 style={{margin:0,fontSize:'1.25rem',fontWeight:800,color:'#111827'}}>Stamm</h1>
        <span style={{fontSize:'0.875rem',color:'#9CA3AF',fontWeight:400}}>· jeudis 20h00</span>
      </div>

      {/* ── Mes inscriptions (dropdown overlay, connecté seulement) ── */}
      {isAuthenticated && (
        <div style={{position:'relative',zIndex:50}}>
          <button
            onClick={()=>setShowMySessions(v=>!v)}
            style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',backgroundColor:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:showMySessions?'10px 10px 0 0':10,cursor:'pointer',gap:8}}
          >
            <div style={{display:'flex',alignItems:'center',gap:7}}>
              <span style={{fontSize:'0.6875rem',fontWeight:700,color:'#1D4ED8',textTransform:'uppercase',letterSpacing:'0.06em'}}>Mes inscriptions</span>
              {mySessions.length > 0 && (
                <span style={{backgroundColor:'#DBEAFE',color:'#1D4ED8',fontSize:'0.625rem',fontWeight:700,padding:'1px 6px',borderRadius:9999}}>{mySessions.length}</span>
              )}
            </div>
            <span style={{fontSize:'0.75rem',color:'#3B82F6',fontWeight:600}}>{showMySessions ? '▲ Réduire' : '▼ Voir'}</span>
          </button>
          {showMySessions && (
            <div style={{position:'absolute',top:'100%',left:0,right:0,border:'1px solid #BFDBFE',borderTop:'none',borderRadius:'0 0 10px 10px',backgroundColor:'#F8FAFF',padding:'10px 12px',display:'flex',flexDirection:'column',gap:6,boxShadow:'0 8px 24px rgba(0,0,0,0.12)'}}>
              {mySessions.length === 0 ? (
                <div style={{fontSize:'0.8125rem',color:'#9CA3AF',textAlign:'center',padding:'8px 0'}}>Aucune inscription en cours.</div>
              ) : mySessions.map(s => {
                const pending = s.statut === 'pending';
                const confirmed = s.statut === 'confirmed';
                return (
                  <div key={s.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,padding:'7px 10px',backgroundColor:'white',borderRadius:7,border:'1px solid #F3F4F6'}}>
                    <div style={{display:'flex',flexDirection:'column',flex:1,minWidth:0}}>
                      <span style={{fontWeight:600,fontSize:'0.8125rem',color:'#111827',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                        {format(new Date(s.date),'EEE d MMM',{locale:fr})}
                      </span>
                      <span style={{fontSize:'0.75rem',color:'#9CA3AF'}}>20h00</span>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
                      {confirmed ? (
                        <span style={{display:'inline-flex',alignItems:'center',gap:4,backgroundColor:'#D1FAE5',color:'#065F46',borderRadius:6,padding:'3px 9px',fontSize:'0.75rem',fontWeight:700}}>
                          <CheckCircle2 style={{width:11,height:11}}/> Confirmé
                        </span>
                      ) : pending ? (
                        <span style={{backgroundColor:'#FEFCE8',color:'#92400E',borderRadius:6,padding:'3px 9px',fontSize:'0.75rem',fontWeight:700}}>En attente</span>
                      ) : null}
                      <button onClick={()=>handleRetirer(s.id)}
                        style={{display:'inline-flex',alignItems:'center',gap:4,padding:'4px 9px',borderRadius:6,border:'1px solid #FECACA',backgroundColor:'#FEF2F2',cursor:'pointer',fontSize:'0.75rem',fontWeight:600,color:'#DC2626'}}>
                        <XCircle style={{width:11,height:11}}/> Retirer
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}



      {/* Liste des sessions */}
      {sessions.length === 0 && (
        <div style={{backgroundColor:'white',border:'1px dashed #E5E7EB',borderRadius:10,padding:'3rem',textAlign:'center'}}>
          <Calendar style={{width:32,height:32,color:'#D1D5DB',margin:'0 auto 10px',display:'block'}}/>
          <p style={{color:'#9CA3AF',fontSize:'0.875rem',margin:0}}>Aucune session programmée pour le moment.</p>
        </div>
      )}

      {sessions.length > 0 && (<>
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {visibleSessions.map(s => {
            const annule = s.statut === 'annule';
            const libre = s.statut === 'libre' || s.statut === 'ouvert';
            const pending = s.statut === 'pending';
            const confirmed = s.statut === 'confirmed';
            const isThisWeek = (new Date(s.date) - new Date()) < 7 * 86400000 && (new Date(s.date) - new Date()) > 0;
            const isMine = user && String(s.responsable_id) === String(user?.id);
            const indResp = s.responsable_indicatif || s.responsable_ind_libre || s.responsable_nom_libre;
            const dateObj = new Date(s.date);
            return (
              <div key={s.id} style={{backgroundColor:'white',border:'1px solid #E5E7EB',borderRadius:10,display:'flex',alignItems:'stretch',overflow:'hidden',opacity:annule?0.65:1}}>

                {/* Colonne date gauche */}
                <div style={{width:58,flexShrink:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'12px 6px',borderRight:'1px solid #F3F4F6',backgroundColor: annule?'#F9FAFB': isThisWeek?'#EEF2FF':'#FAFAFA'}}>
                  <span style={{fontSize:'0.625rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em',color: isThisWeek?'#4F46E5':'#9CA3AF'}}>
                    {format(dateObj,'EEE',{locale:fr}).replace('.','').toUpperCase()}
                  </span>
                  <span style={{fontSize:'1.375rem',fontWeight:800,lineHeight:1,color: annule?'#9CA3AF': isThisWeek?'#4F46E5':'#111827'}}>
                    {format(dateObj,'d',{locale:fr})}
                  </span>
                </div>

                {/* Contenu */}
                <div style={{flex:1,minWidth:0,padding:'11px 14px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:10}}>
                  <div style={{minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                      <span style={{fontWeight:600,fontSize:'0.875rem',color:annule?'#9CA3AF':'#111827',textDecoration:annule?'line-through':'none'}}>
                        {format(dateObj,'MMMM yyyy',{locale:fr})}
                      </span>
                      <span style={{fontSize:'0.75rem',color:'#9CA3AF',display:'flex',alignItems:'center',gap:3}}>
                        <Clock style={{width:11,height:11}}/> 20h00
                      </span>
                    </div>
                    {(isThisWeek && !annule) && (
                      <span style={{display:'inline-block',marginTop:3,backgroundColor:'#EEF2FF',color:'#4F46E5',fontSize:'0.6875rem',fontWeight:700,padding:'2px 8px',borderRadius:20}}>Ce jeudi</span>
                    )}
                    {annule && s.annule_raison && (
                      <div style={{marginTop:3,fontSize:'0.75rem',color:'#DC2626',display:'flex',alignItems:'center',gap:3}}>
                        <AlertTriangle style={{width:10,height:10}}/> {s.annule_raison}
                      </div>
                    )}
                    {annule && !s.annule_raison && (
                      <span style={{display:'inline-flex',marginTop:3,alignItems:'center',gap:3,backgroundColor:'#FEE2E2',color:'#DC2626',fontSize:'0.6875rem',fontWeight:700,padding:'2px 7px',borderRadius:20}}>
                        <AlertTriangle style={{width:10,height:10}}/> Annulé
                      </span>
                    )}
                  </div>

                  {/* Action */}
                  <div style={{flexShrink:0}}>
                    {annule ? null
                    : isMine && pending ? (
                      <button onClick={()=>handleRetirer(s.id)}
                        style={{display:'inline-flex',alignItems:'center',gap:5,padding:'6px 12px',borderRadius:7,border:'1px solid #FECACA',backgroundColor:'#FEF2F2',cursor:'pointer',fontSize:'0.8125rem',fontWeight:600,color:'#DC2626'}}>
                        <XCircle style={{width:13,height:13}}/> Retirer
                      </button>
                    ) : isMine && confirmed ? (
                      <span style={{display:'inline-flex',alignItems:'center',gap:5,backgroundColor:'#D1FAE5',color:'#065F46',borderRadius:7,padding:'6px 12px',fontSize:'0.8125rem',fontWeight:700}}>
                        <CheckCircle2 style={{width:13,height:13}}/> Vous ouvrez
                      </span>
                    ) : libre ? (
                      <button onClick={()=>{ setProposeModal(s); setNom(''); setIndicatif(''); }} disabled={submitting}
                        style={{display:'inline-flex',alignItems:'center',gap:6,backgroundColor:'#6366F1',color:'white',border:'none',borderRadius:7,padding:'7px 14px',fontSize:'0.8125rem',fontWeight:700,cursor:'pointer',opacity:submitting?0.7:1,whiteSpace:'nowrap'}}>
                        <Key style={{width:13,height:13}}/> Ouvrir les locaux
                      </button>
                    ) : confirmed ? (
                      <span style={{fontFamily:'monospace',fontSize:'0.8125rem',fontWeight:700,color:'#15803D',backgroundColor:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:6,padding:'3px 9px'}}>{indResp}</span>
                    ) : pending ? (
                      <span style={{fontFamily:'monospace',fontSize:'0.8125rem',fontWeight:700,color:'#92400E',backgroundColor:'#FEFCE8',border:'1px solid #FDE68A',borderRadius:6,padding:'3px 9px'}}>{indResp}</span>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Accordéon voir plus/moins */}
        {hasMore && (
          <button onClick={()=>setShowAll(v=>!v)} style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,width:'100%',padding:'9px',backgroundColor:'white',border:'1px solid #E5E7EB',borderRadius:10,cursor:'pointer',fontSize:'0.8125rem',fontWeight:600,color:'#6B7280'}}>
            {showAll ? '▲ Voir moins' : `▼ Voir les ${sessions.length - VISIBLE_DEFAULT} soirées suivantes`}
          </button>
        )}
      </>)}

      {/* Modal proposer */}
      {proposeModal && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}} onClick={()=>setProposeModal(null)}>
          <div style={{backgroundColor:'white',borderRadius:12,padding:'24px',maxWidth:420,width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>

            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
              <Key style={{width:18,height:18,color:'#6366F1'}}/>
              <div style={{fontWeight:800,fontSize:'1rem',color:'#111827'}}>Ouvrir les locaux</div>
            </div>
            <div style={{fontSize:'0.875rem',color:'#6366F1',fontWeight:600,marginBottom:16}}>
              {format(new Date(proposeModal.date),'EEEE d MMMM yyyy',{locale:fr})} · 20h00
            </div>

            {!isAuthenticated && (
              <div style={{display:'flex',flexDirection:'column',gap:12,marginBottom:16}}>
                <div>
                  <label style={lbl}>Votre nom *</label>
                  <input style={inp} value={nom} onChange={e=>setNom(e.target.value)} placeholder="Prénom Nom" autoFocus/>
                </div>
                <div>
                  <label style={lbl}>Indicatif *</label>
                  <input style={{...inp,fontFamily:'monospace',textTransform:'uppercase'}} value={indicatif} onChange={e=>setIndicatif(e.target.value.toUpperCase())} placeholder="HB9XX"/>
                </div>
              </div>
            )}

            <div style={{backgroundColor:'#EEF2FF',border:'1px solid #C7D2FE',borderRadius:7,padding:'8px 12px',marginBottom:18,fontSize:'0.8125rem',color:'#3730A3'}}>
              Votre candidature sera <strong>validée par le comité</strong> avant confirmation.
            </div>

            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setProposeModal(null)} style={{padding:'8px 16px',borderRadius:7,border:'1px solid #E5E7EB',background:'white',cursor:'pointer',fontSize:'0.875rem',color:'#374151'}}>Annuler</button>
              <button onClick={handleProposer} disabled={submitting || (!isAuthenticated && (!nom.trim() || !indicatif.trim()))}
                style={{padding:'8px 18px',borderRadius:7,fontSize:'0.875rem',fontWeight:700,cursor:'pointer',backgroundColor:'#6366F1',color:'white',border:'none',
                  opacity:(submitting || (!isAuthenticated && (!nom.trim() || !indicatif.trim()))) ? 0.5 : 1}}>
                {submitting ? 'Envoi…' : "J'ouvre les locaux"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );
};

export default StammPage;
